import React, { useState, useEffect, useRef } from 'react';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

// Notification IDs must be numbers (not strings) for Capacitor/Android compatibility
const NOTIFICATION_IDS = {
  DAILY_CHECKIN: 1001,
  AFTERNOON_ENCOURAGEMENT: 1002,
  EVENING_GRATITUDE: 1003,
};
import { backupRestoreService } from '../../services/backupRestoreService';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Card,
  CardContent,
  Chip,
  IconButton,
  Fade,
  Grow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Zoom,
  Slide,
  keyframes
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ShareIcon from '@mui/icons-material/Share';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { styled } from '@mui/material/styles';

// Helper function to calculate streak
const calculateStreak = (history) => {
  if (!history || history.length === 0) return 0;
  let streak = 0;
  let dayOffset = 0;
  // If today is not in history, check from yesterday
  if (!history.includes(dayjs().format('YYYY-MM-DD'))) {
    dayOffset = 1;
  }
  while (history.includes(dayjs().subtract(dayOffset, 'day').format('YYYY-MM-DD'))) {
    streak++;
    dayOffset++;
  }
  return streak;
};

// Animation keyframes
const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0,0,0);
  }
  40%, 43% {
    transform: translate3d(0, -30px, 0);
  }
  70% {
    transform: translate3d(0, -15px, 0);
  }
  90% {
    transform: translate3d(0, -4px, 0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translate3d(0, 40px, 0);
  }
  to {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

// Styled components with animations
const AnimatedCard = styled(Card)(({ theme }) => ({
  animation: `${fadeInUp} 0.6s ease-out`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const PulsingButton = styled(Button)(({ theme }) => ({
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    animation: 'none',
  },
}));

const BouncingIcon = styled(Box)(() => ({
  animation: `${bounce} 2s infinite`,
}));

const ShimmerCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(90deg, ${theme.palette.background.paper} 0px, rgba(255,255,255,0.8) 40px, ${theme.palette.background.paper} 80px)`,
  backgroundSize: '600px',
  animation: `${shimmer} 1.6s infinite linear`,
}));

// Mood options with emojis (sorted from most positive to negative)
const MOOD_OPTIONS = [
  { emoji: '🤩', label: 'Ecstatic', value: 'ecstatic', color: '#FFD700' },
  { emoji: '😍', label: 'Excited', value: 'excited', color: '#FF69B4' },
  { emoji: '😊', label: 'Happy', value: 'happy', color: '#4CAF50' },
  { emoji: '😎', label: 'Cool', value: 'cool', color: '#2196F3' },
  { emoji: '😐', label: 'Neutral', value: 'neutral', color: '#9E9E9E' },
  { emoji: '😤', label: 'Frustrated', value: 'frustrated', color: '#FF5722' },
  { emoji: '😢', label: 'Sad', value: 'sad', color: '#3F51B5' },
  { emoji: '😡', label: 'Angry', value: 'angry', color: '#F44336' }
];

// Inspirational quotes based on mood
const INSPIRATIONAL_QUOTES = {
  ecstatic: [
    "You're absolutely unstoppable today! Your energy is incredible!",
    "This level of joy is contagious! You're lighting up the world!",
    "Your enthusiasm is your superpower! Keep soaring!",
    "Amazing things happen when you feel this alive!",
    "You're radiating pure magic today! Keep shining bright!"
  ],
  excited: [
    "Your excitement is the fuel for extraordinary achievements!",
    "Channel this energy into something amazing today!",
    "Excitement is the spark that ignites great adventures!",
    "Your enthusiasm is inspiring! Keep that fire burning!",
    "Great things are coming your way! Stay excited!"
  ],
  happy: [
    "Your positive energy is contagious. Keep shining!",
    "Happiness is not a destination, it's a way of life.",
    "Today's joy is tomorrow's strength.",
    "Your smile can change the world. Keep smiling!",
    "Celebrate every small victory. You deserve it!"
  ],
  cool: [
    "Confidence is your best accessory. Wear it well.",
    "Stay cool, stay focused, stay amazing.",
    "You've got this! Your confidence is inspiring.",
    "Cool minds achieve great things.",
    "Your calm energy is your superpower."
  ],
  neutral: [
    "Sometimes neutral is exactly where you need to be.",
    "Balance is beautiful. You're finding your center.",
    "Steady and calm - you're building inner strength.",
    "It's okay to feel balanced. Peace has its own power.",
    "Neutral doesn't mean nothing - it means you're grounded."
  ],
  frustrated: [
    "Every challenge is an opportunity to grow stronger.",
    "Frustration is the first step towards improvement.",
    "You are stronger than you think and more capable than you imagine.",
    "This too shall pass. Keep pushing forward.",
    "Turn your frustration into fuel for your success."
  ],
  sad: [
    "It's okay to feel sad. Tomorrow brings new hope.",
    "Every storm runs out of rain. Brighter days are coming.",
    "Your feelings are valid. Take time to heal.",
    "You are not alone. This feeling will pass.",
    "Be gentle with yourself. You're doing better than you think."
  ],
  angry: [
    "Channel your anger into positive action.",
    "Your passion can move mountains when directed right.",
    "Take a deep breath. You have the power to choose your response.",
    "Anger is energy. Use it to fuel positive change.",
    "You are in control of your emotions, not the other way around."
  ]
};

function DailyCheckInNew() {
  // Existing states
  const [checkInHistory, setCheckInHistory] = useState(() => {
    const saved = localStorage.getItem('checkInHistory');
    return saved ? JSON.parse(saved) : [];
  });

  const [streak, setStreak] = useState(0);
  const [medals, setMedals] = useState(() => {
    const saved = localStorage.getItem('checkInMedals');
    return saved ? JSON.parse(saved) : 0;
  });

  // New states for mood and quotes
  const [moodHistory, setMoodHistory] = useState(() => {
    const saved = localStorage.getItem('moodHistory');
    return saved ? JSON.parse(saved) : {};
  });

  const [selectedMood, setSelectedMood] = useState(null);
  const [todayQuote, setTodayQuote] = useState('');
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);

  // Existing states
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [openMedalDialog, setOpenMedalDialog] = useState(false);
  const [showMonthlyView, setShowMonthlyView] = useState(false);
  const [showDevTools, setShowDevTools] = useState(process.env.NODE_ENV === 'development');
  const [isBackupLoading, setIsBackupLoading] = useState(false);

  const fileInputRef = useRef(null);

  const handleExportBackup = async () => {
    try {
      setIsBackupLoading(true);
      setSnackbarMessage('正在準備備份資料，這可能需要一點時間...');
      setSnackbarSeverity('info');
      setOpenSnackbar(true);
      
      await backupRestoreService.exportBackup();
      
      setSnackbarMessage('備份檔已成功匯出！');
      setSnackbarSeverity('success');
    } catch (error) {
      console.error(error);
      setSnackbarMessage(`備份失敗: ${error.message}`);
      setSnackbarSeverity('error');
    } finally {
      setIsBackupLoading(false);
      setOpenSnackbar(true);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsBackupLoading(true);
      setSnackbarMessage('正在還原資料，請勿關閉 App...');
      setSnackbarSeverity('info');
      setOpenSnackbar(true);

      await backupRestoreService.importBackup(file);
      
      setSnackbarMessage('還原成功！正在重新載入...');
      setSnackbarSeverity('success');
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error(error);
      setSnackbarMessage(`還原失敗: ${error.message}`);
      setSnackbarSeverity('error');
    } finally {
      setIsBackupLoading(false);
      setOpenSnackbar(true);
      // 清空 input 讓同一個檔案可以再次被選取
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const today = dayjs().format('YYYY-MM-DD');
  const hasCheckedInToday = checkInHistory.includes(today);
  const todayMood = moodHistory[today];

  // Calculate streak (now using helper function)
  useEffect(() => {
    setStreak(calculateStreak(checkInHistory));
    localStorage.setItem('checkInHistory', JSON.stringify(checkInHistory));
  }, [checkInHistory]);

  // Save mood history to localStorage
  useEffect(() => {
    localStorage.setItem('moodHistory', JSON.stringify(moodHistory));
  }, [moodHistory]);

  // Initialize notifications on component mount
  useEffect(() => {
    initializeNotifications();
  }, []);

  // Notification functions
  const requestNotificationPermissions = async () => {
    try {
      const result = await LocalNotifications.requestPermissions();
      return result.display === 'granted';
    } catch (error) {
      console.error('Notification permission request failed:', error);
      return false;
    }
  };

    const scheduleDailyReminder = async () => {
    try {
      // Clear existing daily reminders
      await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_IDS.DAILY_CHECKIN }] });

      const encouragingMessages = [
        "A new day, a new promise to yourself. You've got this, Jessica! ✨",
        "Jessica, today is another chance to shine. Keep your promise! 🌟",
        "Stay positive, stay strong. Remember the promise you made to yourself, Jessica. 💪",
        "Your journey, your promise. Make today count, Jessica! 💖",
        "Jessica, let your actions honor your promises. You are capable of amazing things. 🚀",
        "Keep that promise to yourself, Jessica. Every step forward is a victory. 🏆",
        "A beautiful day to keep a beautiful promise. You can do it, Jessica! ☀️",
        "Jessica, your dedication is your strength. Keep honoring your promise. 🙏",
        "Let your positivity guide you today, Jessica. Remember your promise. 😊",
        "One day at a time, one promise kept. You're doing great, Jessica! 🎉",
        "Jessica, believe in the promise of a new day and in yourself. ✨",
        "Your promise is a gift to your future self. Keep going, Jessica! 🎁",
        "Stay true to your goals, Jessica. Your promise is worth it. 💯",
        "Jessica, let your inner light shine and keep that promise bright.💡",
        "Embrace the day with a positive heart, Jessica. Your promise awaits. ❤️",
        "You are powerful, Jessica. Keep the promise you made to your amazing self. 🦋",
        "Let today be a testament to your strength. Keep your promise, Jessica. 🌿",
        "Jessica, you're building a better you, one promise at a time. Keep it up! 🏗️"
      ];

      const randomMessage = encouragingMessages[Math.floor(Math.random() * encouragingMessages.length)];
      
      // Set daily 6:30 AM reminder
      await LocalNotifications.schedule({
        notifications: [
          {
            title: 'Jessica\'s Amazing Day start!',
            body: randomMessage,
            id: NOTIFICATION_IDS.DAILY_CHECKIN,
            channelId: 'daily-reminders',
            schedule: {
              on: {
                hour: 6,
                minute: 30
              },
              repeats: true,
              allowWhileIdle: true
            },
            sound: 'default',
            smallIcon: 'ic_stat_icon_config_sample',
            iconColor: '#488AFF',
            attachments: null,
            actionTypeId: '',
            extra: null
          }
        ]
      });
      console.log('Daily reminder set successfully with a random message.');
    } catch (error) {
      console.error('Failed to set daily reminder:', error);
    }
  };

  const scheduleAfternoonReminder = async () => {
    try {
      // Clear existing afternoon reminders
      await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_IDS.AFTERNOON_ENCOURAGEMENT }] });

      const encouragingWords = [
        "Keep going, Jessica! You're doing great today. ✨",
        "Halfway through the day! Your strength is inspiring. 💪",
        "A little afternoon boost for an amazing person! Keep shining, Jessica. 🌟",
        "Remember your goals, Jessica. You're on the right track! 💖",
        "Just a little reminder: You are awesome, Jessica! 🚀",
        "Keep up the fantastic work, Jessica! Every effort counts. 🏆",
        "Sending some positive vibes your way, Jessica! ☀️",
        "Your dedication is paying off, Jessica. Keep it up! 🙏",
        "You've got this, Jessica! Finish the day strong. 😊",
        "A moment to pause and appreciate how far you've come today, Jessica. 🎉",
        "Believe in yourself, Jessica. You're capable of anything. ✨",
        "Your positive energy is making a difference, Jessica! 🎁",
        "Stay focused and keep shining, Jessica. You're a star. 💯",
        "Let your afternoon be as bright as your spirit, Jessica. 💡",
        "You are making today a masterpiece, Jessica. ❤️",
        "Keep flying high, Jessica. You are unstoppable. 🦋",
        "Your progress today is something to be proud of, Jessica. 🌿",
        "Keep building your dreams, Jessica. You're doing wonderfully. 🏗️"
      ];

      const randomWord = encouragingWords[Math.floor(Math.random() * encouragingWords.length)];

      // Set daily 1 PM reminder
      await LocalNotifications.schedule({
        notifications: [
          {
            title: 'A Little Afternoon Boost for Jessica! ☀️',
            body: randomWord,
            id: NOTIFICATION_IDS.AFTERNOON_ENCOURAGEMENT,
            channelId: 'daily-reminders',
            schedule: {
              on: {
                hour: 13,
                minute: 0
              },
              repeats: true,
              allowWhileIdle: true
            },
            sound: 'default',
            smallIcon: 'ic_stat_icon_config_sample',
            iconColor: '#488AFF',
            attachments: null,
            actionTypeId: '',
            extra: null
          }
        ]
      });
      console.log('Afternoon encouragement reminder set successfully.');
    } catch (error) {
      console.error('Failed to set afternoon reminder:', error);
    }
  };

  const scheduleEveningGratitude = async () => {
    try {
      // Clear existing evening gratitude reminders
      await LocalNotifications.cancel({ notifications: [{ id: NOTIFICATION_IDS.EVENING_GRATITUDE }] });

      const gratitudeMessages = [
        "Great work today, Jessica! Time to relax and enjoy life! 🌅",
        "Productive day! Time to unwind and celebrate. You deserve it! 🎉",
        "You worked hard today! Time to enjoy your personal time! ✨",
        "Amazing dedication today! Now enjoy the evening - it's your time! 🌙",
        "Be grateful for today's efforts! Time to relax and enjoy! 💖",
        "Inspiring work today! Time to enjoy life's beautiful moments! 🌸",
        "Thank yourself for today's dedication! Enjoy this peaceful evening! 🕊️",
        "Wonderful day of work! Time to appreciate life's simple pleasures! 🌺",
        "Remarkable commitment today! Time to enjoy your personal time! 🌟",
        "You worked hard today! Time to enjoy life's beautiful moments! 🌈",
        "Admirable efforts today! Time to switch gears and enjoy the evening! 🎨",
        "Reflect on today's achievements! Time to relax and appreciate life! 🌻",
        "Be proud of today's work! Time to enjoy personal happiness! 💫",
        "Beautiful dedication today! Time to be grateful and enjoy the evening! 🌅",
        "Thank yourself for today's efforts! Time to unwind and enjoy life! 🎁",
        "Amazing day of work! Time to appreciate this peaceful evening! 🕯️",
        "Truly special work today! Time to relax and be grateful for life! 🌙",
        "Appreciate yourself, Jessica! You worked hard. Time to celebrate! 🎊"
      ];

      const randomGratitude = gratitudeMessages[Math.floor(Math.random() * gratitudeMessages.length)];

      // Set daily 7 PM gratitude reminder
      await LocalNotifications.schedule({
        notifications: [
          {
            title: 'Time to Be Grateful, Jessica! 🙏',
            body: randomGratitude,
            id: NOTIFICATION_IDS.EVENING_GRATITUDE,
            channelId: 'daily-reminders',
            schedule: {
              on: {
                hour: 19,
                minute: 0
              },
              repeats: true,
              allowWhileIdle: true
            },
            sound: 'default',
            smallIcon: 'ic_stat_icon_config_sample',
            iconColor: '#488AFF',
            attachments: null,
            actionTypeId: '',
            extra: null
          }
        ]
      });
      console.log('Evening gratitude reminder set successfully.');
    } catch (error) {
      console.error('Failed to set evening gratitude reminder:', error);
    }
  };

  const initializeNotifications = async () => {
    // 只在原生平台（Android/iOS）上執行通知邏輯
    if (!Capacitor.isNativePlatform()) {
      console.log('Notifications only available on native platform, skipping...');
      return;
    }

    try {
      const granted = await requestNotificationPermissions();
      if (granted) {
        // Create notification channel for Android
        if (Capacitor.getPlatform() === 'android') {
          try {
            await LocalNotifications.createChannel({
              id: 'daily-reminders',
              name: 'Daily Reminders',
              description: 'Daily check-in and encouragement reminders',
              importance: 5,
              visibility: 1,
              vibration: true,
              lights: true,
              lightColor: '#FF0000'
            });
            console.log('Notification channel created successfully');
          } catch (error) {
            console.error('Channel creation failed:', error);
          }
        }
        
        // Check exact alarm setting for Android 12+
        try {
          const exactSetting = await LocalNotifications.checkExactNotificationSetting();
          if (exactSetting && exactSetting.exact === 'denied') {
            setSnackbarMessage('For precise reminders, please enable "Alarms & reminders" in app settings.');
            setSnackbarSeverity('warning');
            setOpenSnackbar(true);
          }
        } catch (error) {
          console.log('Exact notification setting check not available:', error);
        }
        
        // Check if notifications are already scheduled to avoid redundant re-scheduling
        try {
          const pending = await LocalNotifications.getPending();
          const pendingIds = pending.notifications.map(n => n.id);
          
          const needsDaily = !pendingIds.includes(NOTIFICATION_IDS.DAILY_CHECKIN);
          const needsAfternoon = !pendingIds.includes(NOTIFICATION_IDS.AFTERNOON_ENCOURAGEMENT);
          const needsEvening = !pendingIds.includes(NOTIFICATION_IDS.EVENING_GRATITUDE);
          
          if (needsDaily) await scheduleDailyReminder();
          if (needsAfternoon) await scheduleAfternoonReminder();
          if (needsEvening) await scheduleEveningGratitude();
          
          if (needsDaily || needsAfternoon || needsEvening) {
            setSnackbarMessage('Reminders set for 6:30 AM, 1 PM, and 7 PM! You got this, Jessica!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
          } else {
            console.log('All notifications already scheduled, skipping...');
          }
        } catch (error) {
          console.error('Failed to schedule notifications:', error);
          setSnackbarMessage(`通知設定失敗: ${error.message || '未知錯誤'}`);
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
        }
      } else {
        setSnackbarMessage('Please enable notification permissions to receive daily reminders.');
        setSnackbarSeverity('warning');
        setOpenSnackbar(true);
      }
    } catch (error) {
        console.error('Failed to initialize notifications:', error);
        setSnackbarMessage(`通知初始化失敗: ${error.message || '未知錯誤'}`);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
  };

  const sendCheckInSuccessNotification = async (streakCount) => {
    try {
      await LocalNotifications.schedule({
        notifications: [
          {
            title: 'Check-in Successful! 🎉',
            body: `Amazing! You've checked in for ${streakCount} consecutive days! Keep it up!`,
            id: Math.floor(Math.random() * 2147483647), // Use random int within Java int range
            channelId: 'daily-reminders',
            schedule: { at: new Date(Date.now() + 2000) }, // Show after 2 seconds
            sound: 'default',
            smallIcon: 'ic_stat_icon_config_sample',
            iconColor: '#488AFF',
            attachments: null,
            actionTypeId: '',
            extra: null
          }
        ]
      });
    } catch (error) {
      console.error('Failed to send check-in success notification:', error);
    }
  };

  // Handle mood selection
  const handleMoodSelect = (mood) => {
    if (hasCheckedInToday) {
      setSnackbarMessage("Jessica, you've already checked in today! You're on fire! 🔥");
      setSnackbarSeverity('info');
      setOpenSnackbar(true);
      return;
    }

    setSelectedMood(mood);
    const newMoodHistory = { ...moodHistory, [today]: mood };
    setMoodHistory(newMoodHistory);
    
    // Generate random quote based on mood
    const quotes = INSPIRATIONAL_QUOTES[mood.value];
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setTodayQuote(randomQuote);
    
    setSnackbarMessage(`Perfect choice, Jessica! ${mood.label} ${mood.emoji} - You're amazing!`);
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
  };

  // Developer Testing Functions
  const devTools = {
    // Populate test data for the last 30 days
    populateTestData: () => {
      const testHistory = [];
      const testMoods = {};
      
      for (let i = 0; i < 30; i++) {
        const date = dayjs().subtract(i, 'day').format('YYYY-MM-DD');
        // 80% chance of check-in
        if (Math.random() > 0.2) {
          testHistory.push(date);
          // Random mood selection
          const randomMood = MOOD_OPTIONS[Math.floor(Math.random() * MOOD_OPTIONS.length)];
          testMoods[date] = randomMood;
        }
      }
      
      setCheckInHistory(testHistory);
      setMoodHistory(testMoods);
      localStorage.setItem('checkInHistory', JSON.stringify(testHistory));
      localStorage.setItem('moodHistory', JSON.stringify(testMoods));
      
      setSnackbarMessage('Test data populated for 30 days!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    },
    
    // Clear all data
    clearAllData: () => {
      setCheckInHistory([]);
      setMoodHistory({});
      setStreak(0);
      setMedals(0);
      localStorage.removeItem('checkInHistory');
      localStorage.removeItem('moodHistory');
      localStorage.removeItem('checkInMedals');
      
      setSnackbarMessage('All data cleared!');
      setSnackbarSeverity('info');
      setOpenSnackbar(true);
    },
    
    // Simulate long streak
    simulateLongStreak: () => {
      const streakHistory = [];
      for (let i = 14; i >= 0; i--) {
        streakHistory.push(dayjs().subtract(i, 'day').format('YYYY-MM-DD'));
      }
      
      setCheckInHistory(streakHistory);
      localStorage.setItem('checkInHistory', JSON.stringify(streakHistory));
      
      setSnackbarMessage('15-day streak simulated!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    },
    
    // Trigger medal dialog
    showMedalDialog: () => {
      setOpenMedalDialog(true);
    },
    
    // Show monthly view
    showMonthlyViewDialog: () => {
      setShowMonthlyView(true);
    },
    
    // Add medals
    addMedals: (count = 5) => {
      const newMedals = medals + count;
      setMedals(newMedals);
      localStorage.setItem('checkInMedals', newMedals);
      
      setSnackbarMessage(`Added ${count} medals! Total: ${newMedals}`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    },
    
    // Test all moods
    testAllMoods: () => {
      const testMoods = {};
      MOOD_OPTIONS.forEach((mood, index) => {
        const date = dayjs().subtract(index, 'day').format('YYYY-MM-DD');
        testMoods[date] = mood;
      });
      
      setMoodHistory(prev => ({ ...prev, ...testMoods }));
      localStorage.setItem('moodHistory', JSON.stringify({ ...moodHistory, ...testMoods }));
      
      setSnackbarMessage('All mood types added to history!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    },
    
    // Test notifications
    testNotification: async () => {
      try {
        await LocalNotifications.schedule({
          notifications: [
            {
              title: 'Test Notification 🔔',
              body: 'This is a test notification to confirm local notification functionality is working!',
              id: Math.floor(Math.random() * 2147483647), // Use random int within Java int range
              channelId: 'daily-reminders',
              schedule: { at: new Date(Date.now() + 3000) }, // Show after 3 seconds
              sound: 'default',
              smallIcon: 'ic_stat_icon_config_sample',
              iconColor: '#488AFF',
              attachments: null,
              actionTypeId: '',
              extra: null
            }
          ]
        });
        setSnackbarMessage('Test notification scheduled, will appear in 3 seconds!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        setSnackbarMessage('Test notification failed: ' + error.message);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    },
    
    // Reset daily reminder
    resetDailyReminders: async () => {
      try {
        await scheduleDailyReminder();
        await scheduleAfternoonReminder();
        setSnackbarMessage('All daily reminders have been reset!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        setSnackbarMessage('Failed to reset reminders: ' + error.message);
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    }
  };

  // Handle check-in
  const handleCheckIn = () => {
    if (hasCheckedInToday) {
      setSnackbarMessage("Jessica, you've already checked in today! You're absolutely crushing it! 🌟");
      setSnackbarSeverity('info');
      setOpenSnackbar(true);
      return;
    }

    if (!todayMood) {
      setSnackbarMessage('Jessica, please select your mood first! We want to celebrate your feelings! 💭✨');
      setSnackbarSeverity('warning');
      setOpenSnackbar(true);
      return;
    }
    
    const newHistory = [...checkInHistory, today];
    setCheckInHistory(newHistory);
    
    const newStreak = calculateStreak(newHistory);
    
    // Show inspirational quote
    const quotes = INSPIRATIONAL_QUOTES[todayMood.value];
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setTodayQuote(randomQuote);
    setShowQuoteDialog(true);
    
    // Check-in success notification removed
    
    // Check for medal
    if (newStreak > 0 && newStreak % 7 === 0) {
      const newMedals = medals + 1;
      setMedals(newMedals);
      localStorage.setItem('checkInMedals', newMedals);
      setTimeout(() => setOpenMedalDialog(true), 2000); // Show medal dialog after quote
    }
  };

  // Generate past days display
  const renderPastDays = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const date = dayjs().subtract(i, 'day');
      const dateStr = date.format('YYYY-MM-DD');
      const isChecked = checkInHistory.includes(dateStr);
      const dayMood = moodHistory[dateStr];

      days.push(
        <Grid item key={dateStr} xs={12/7}>
          <Paper
            elevation={isChecked ? 3 : 1}
            sx={{
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isChecked ? '#E3F2FD' : 'background.paper',
              borderRadius: 2,
              minHeight: '70px',
              textAlign: 'center',
              border: dateStr === today ? '2px solid #2196F3' : 'none'
            }}
          >
            <Typography variant="caption" fontWeight="bold">
              {date.format('DD')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              {date.format('ddd')}
            </Typography>
            {dayMood && (
              <Typography sx={{ fontSize: '1.2rem', mt: 0.5 }}>
                {dayMood.emoji}
              </Typography>
            )}
            {isChecked && (
              <CheckCircleIcon color="success" sx={{ fontSize: '1rem', mt: 0.5 }} />
            )}
          </Paper>
        </Grid>
      );
    }
    return days;
  };

  return (
    <Box sx={{ 
      // background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      minHeight: '100vh',
      borderRadius: 2,
      marginTop: '-20px',
      p: 2
    }}>
   {/* Medals Display */}
      {medals >= 0 && (
        <Fade in={true} timeout={2200}>
          <Box sx={{ mt: 0, mb: 2, textAlign: 'center' }}>
            <AnimatedCard 
              onClick={() => setOpenMedalDialog(true)}
              sx={{ 
              background: 'linear-gradient(135deg, #FFD700 0%,rgb(255, 128, 0) 100%)',
              borderRadius: 3,
              p: 2,
              cursor: 'pointer'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="h4" sx={{ mr: 1 }}>🏆</Typography>
                <Box>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                    Jessica's Achievements
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {medals} Medal{medals > 1 ? 's' : ''} Earned! 🎉
                  </Typography>
                </Box>
              </Box>
            </AnimatedCard>
          </Box>
        </Fade>
      )}
        {/* Mood Selection */}
        <Grow in={true} timeout={1200}>
          <AnimatedCard sx={{ borderRadius: 3, mb: 2, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' }}>
            <CardContent sx={{ pb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center', color: '#333' }}>
              👋Jessica, how are you feeling today?
              </Typography>
            <Grid container spacing={1} justifyContent="center">
                {MOOD_OPTIONS.map((mood, index) => (
                  <Grid item key={mood.value}>
                    <Zoom in={true} timeout={800 + index * 100}>
                      <IconButton
                        onClick={() => handleMoodSelect(mood)}
                        disabled={hasCheckedInToday}
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: todayMood?.value === mood.value ? mood.color : 'white',
                          border: '3px solid',
                          borderColor: todayMood?.value === mood.value ? mood.color : '#E0E0E0',
                          fontSize: '1.8rem',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            bgcolor: hasCheckedInToday ? undefined : `${mood.color}20`,
                            borderColor: mood.color,
                            transform: 'scale(1.1) rotate(5deg)',
                            boxShadow: `0 8px 25px ${mood.color}40`
                          },
                          '&:disabled': {
                            opacity: todayMood?.value === mood.value ? 1 : 0.3
                          },
                          '&:active': {
                            transform: 'scale(0.95)'
                          }
                        }}
                      >
                        {mood.emoji}
                      </IconButton>
                    </Zoom>
                  </Grid>
                ))}
              </Grid>
              {todayMood && (
                <Fade in={true} timeout={1500}>
                  <Box sx={{ mt: 2, textAlign: 'center', p: 2, bgcolor: `${todayMood.color}15`, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ color: todayMood.color, fontWeight: 'bold' }}>
                      {todayMood.emoji} Feeling {todayMood.label}!
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      You're doing great! Keep Postive! 🌟
                    </Typography>
                  </Box>
                </Fade>
              )}
          </CardContent>
         </AnimatedCard>
        </Grow>

      {/* Check-in Button */}
      <Zoom in={true} timeout={900}>
        <Box sx={{ mb: 3 }}>
          {!hasCheckedInToday ? (
            <PulsingButton
              variant="contained"
              fullWidth
              onClick={handleCheckIn}
              sx={{
                py: 3,
                borderRadius: 4,
                fontSize: '1.3rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #FF6B6B, #FF8E8E)',
                boxShadow: '0 8px 32px rgba(255, 107, 107, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FF5252, #FF7979)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(255, 107, 107, 0.6)'
                }
              }}
            >
              🌟 Check in now! 🌟
            </PulsingButton>
          ) : (
            <Button
              variant="contained"
              fullWidth
              disabled
              sx={{
                py: 3,
                borderRadius: 4,
                fontSize: '1rem',
                background: 'linear-gradient(45deg,rgb(64, 30, 187),rgb(45, 35, 178))',
                boxShadow: '0 8px 32px rgba(84, 76, 175, 0.4)',
                '&.Mui-disabled': {
                  background: 'linear-gradient(45deg,rgb(127, 168, 203),rgb(118, 166, 206))',
                  color: 'white'
                }
              }}
            >
              ✅ Amazing work today! ✅
            </Button>
          )}
        </Box>
      </Zoom>

      {/* Weekly Progress */}
      <Grow in={true} timeout={950}>
        <AnimatedCard sx={{ 
          borderRadius: 3, 
          mb: 3,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>🎯</Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>
                  Checkin Journey
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                color="primary" 
                sx={{ 
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    transition: 'transform 0.2s'
                  }
                }}
                onClick={() => {
                  setShowMonthlyView(true);
                }}
              >
                See all &gt;
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, textAlign: 'center' }}>
              "Consistency is the key to success, SPY! 🗝️"
            </Typography>
            <Grid container spacing={0.5}>
              {renderPastDays()}
            </Grid>
          </CardContent>
        </AnimatedCard>
      </Grow>

      {/* Check-in Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <Grow in={true} timeout={1100}>
            <AnimatedCard sx={{ 
              borderRadius: 3, 
              height: '100%',
              background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>📊</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Progress
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Check-in
                </Typography>
                <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                  {checkInHistory.filter(date => dayjs().diff(dayjs(date), 'day') <= 30).length}/30 days
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Crushing it! 💪
                </Typography>
              </CardContent>
            </AnimatedCard>
          </Grow>
        </Grid>
        <Grid item xs={6}>
          <Grow in={true} timeout={1300}>
            <AnimatedCard sx={{ 
              borderRadius: 3, 
              height: '100%',
              background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
              color: 'white'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ mr: 1 }}>🎭</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Lead Mood
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Most pick
                </Typography>
                <Typography variant="h5" sx={{ mt: 1, fontWeight: 'bold' }}>
                  {(() => {
                    const moodCounts = {};
                    Object.values(moodHistory).forEach(mood => {
                      moodCounts[mood.value] = (moodCounts[mood.value] || 0) + 1;
                    });
                    const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0];
                    const moodOption = MOOD_OPTIONS.find(m => m.value === topMood?.[0]);
                    return topMood ? `${moodOption?.emoji} x${topMood[1]}` : '😊 x0';
                  })()}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  Keep shining! ✨
                </Typography>
              </CardContent>
            </AnimatedCard>
          </Grow>
        </Grid>
      </Grid>





      {/* Quote Dialog */}
      <Dialog
        open={showQuoteDialog}
        onClose={() => setShowQuoteDialog(false)}
        TransitionComponent={Zoom}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="h4" sx={{ mb: 1 }}>✨</Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Jessica's Daily Inspiration</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Zoom in={showQuoteDialog} timeout={800}>
              <Typography variant="h2" sx={{ mb: 3 }}>
                {todayMood?.emoji}
              </Typography>
            </Zoom>
            <Fade in={showQuoteDialog} timeout={1200}>
              <Typography variant="h6" sx={{ mb: 3, fontStyle: 'italic', lineHeight: 1.8, opacity: 0.95 }}>
                "{todayQuote}"
              </Typography>
            </Fade>
            <Slide direction="up" in={showQuoteDialog} timeout={1600}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                You're absolutely amazing, Jessica! 🌟💪
              </Typography>
            </Slide>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <PulsingButton
            onClick={() => setShowQuoteDialog(false)}
            variant="contained"
            sx={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)',
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              '&:hover': {
                background: 'rgba(255,255,255,0.3)',
                transform: 'scale(1.05)'
              }
            }}
          >
            Thanks! Let's go! 🚀
          </PulsingButton>
        </DialogActions>
      </Dialog>

      {/* Medal Dialog */}
      <Dialog
        open={openMedalDialog}
        onClose={() => setOpenMedalDialog(false)}
        TransitionComponent={Zoom}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: 'white',
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          <BouncingIcon>
            <Typography variant="h3" sx={{ mb: 2 }}>🎉</Typography>
          </BouncingIcon>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Congratulations Jessica!</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
            <Zoom in={openMedalDialog} timeout={1000}>
              <Box sx={{ position: 'relative' }}>
                <EmojiEventsIcon
                  sx={{ 
                    fontSize: 120, 
                    mb: 3,
                    color: '#FFF',
                    filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.3))',
                    animation: `${pulse} 2s infinite`
                  }}
                />
              </Box>
            </Zoom>
            <Fade in={openMedalDialog} timeout={1500}>
              <Typography variant="h8" sx={{ mb: 2, }}>
                On achieving {streak} consecutive check-ins! You deserve a medal!
              </Typography>
            </Fade>
            <Slide direction="up" in={openMedalDialog} timeout={2000}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Amazing! Keep it up and work towards the next 7 days! 💪
              </Typography>
            </Slide>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <PulsingButton 
            onClick={() => setOpenMedalDialog(false)} 
            variant="contained"
            sx={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.3)',
              color: 'white',
              fontWeight: 'bold',
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontSize: '1.1rem',
              '&:hover': {
                background: 'rgba(255,255,255,0.3)',
                transform: 'scale(1.05)'
              }
            }}
          >
            AWESOME! 🚀
          </PulsingButton>
        </DialogActions>
      </Dialog>

      {/* Monthly View Dialog */}
      <Dialog
        open={showMonthlyView}
        onClose={() => setShowMonthlyView(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="h5">Monthly Check-in Overview</Typography>
          <Typography variant="body2" color="text.secondary">
            {dayjs().format('MMMM YYYY')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {/* Week day headers */}
            <Grid container spacing={1} sx={{ p: 2, pb: 1 }}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <Grid item xs={12/7} key={day}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 30,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#F5F5F5',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>
                      {day}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            
            {/* Calendar days */}
            <Grid container spacing={1} sx={{ px: 2, pb: 2 }}>
              {(() => {
                const daysInMonth = dayjs().daysInMonth();
                const firstDay = dayjs().startOf('month');
                const startOfWeek = firstDay.day(); // 0 = Sunday, 1 = Monday, etc.
                const mondayStart = startOfWeek === 0 ? 6 : startOfWeek - 1; // Convert to Monday = 0
                const days = [];
                
                // Add empty cells for days before the first day of the month
                for (let i = 0; i < mondayStart; i++) {
                  days.push(
                    <Grid item xs={12/7} key={`empty-${i}`}>
                      <Box sx={{ width: '100%', height: 60 }} />
                    </Grid>
                  );
                }
                
                // Add actual days of the month
                for (let i = 1; i <= daysInMonth; i++) {
                  const currentDate = firstDay.add(i - 1, 'day');
                  const dateStr = currentDate.format('YYYY-MM-DD');
                  const isCheckedIn = checkInHistory.includes(dateStr);
                  const dayMood = moodHistory[dateStr];
                  const isToday = dateStr === today;
                  
                  days.push(
                    <Grid item xs={12/7} key={i}>
                      <Box
                        sx={{
                          width: '100%',
                          height: 60,
                          border: '1px solid #E0E0E0',
                          borderRadius: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: isToday ? '#E3F2FD' : isCheckedIn ? '#E8F5E8' : 'white',
                          position: 'relative'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          {i}
                        </Typography>
                        {dayMood && (
                          <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                            {dayMood.emoji}
                          </Typography>
                        )}
                        {isCheckedIn && !dayMood && (
                          <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 16 }} />
                        )}
                      </Box>
                    </Grid>
                  );
                }
                return days;
              })()}
            </Grid>
          
          {/* Legend */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#F5F5F5', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Legend:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, bgcolor: '#E8F5E8', border: '1px solid #E0E0E0', borderRadius: 1, mr: 1 }} />
                <Typography variant="caption">Checked in</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ width: 16, height: 16, bgcolor: '#E3F2FD', border: '1px solid #E0E0E0', borderRadius: 1, mr: 1 }} />
                <Typography variant="caption">Today</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ fontSize: '1rem', mr: 1 }}>😊</Typography>
                <Typography variant="caption">Mood selected</Typography>
              </Box>
            </Box>
          </Box>
          
          {/* Monthly Stats */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#F0F8FF', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Monthly Statistics:</Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">Check-ins this month:</Typography>
                <Typography variant="h6" color="primary">
                  {checkInHistory.filter(date => dayjs(date).month() === dayjs().month()).length} days
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">Most used mood:</Typography>
                <Typography variant="h6">
                  {(() => {
                    const thisMonthMoods = Object.entries(moodHistory)
                      .filter(([date]) => dayjs(date).month() === dayjs().month())
                      .map(([, mood]) => mood);
                    const moodCounts = {};
                    thisMonthMoods.forEach(mood => {
                      moodCounts[mood.value] = (moodCounts[mood.value] || 0) + 1;
                    });
                    const topMood = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0];
                    const moodOption = MOOD_OPTIONS.find(m => m.value === topMood?.[0]);
                    return topMood ? `${moodOption?.emoji} ${moodOption?.label}` : 'No mood data';
                  })()}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            onClick={() => setShowMonthlyView(false)} 
            variant="contained"
            sx={{ borderRadius: 3, px: 4 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Data Backup & Restore (App Only) */}
      {Capacitor.isNativePlatform() && (
        <Card sx={{ mt: 3, mb: showDevTools ? 0 : 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ mr: 1 }}>💾</Typography>
              <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                Data Backup & Restore
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Safely backup all your data (including Vision Boards, check-ins, and Calorie records) to avoid data loss when changing devices.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={isBackupLoading}
                  onClick={handleExportBackup}
                  sx={{ borderRadius: 2, bgcolor: '#2196F3', '&:hover': { bgcolor: '#1976D2' } }}
                >
                  {isBackupLoading ? 'Exporting...' : 'Export Backup'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  disabled={isBackupLoading}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ borderRadius: 2, color: '#FF9800', borderColor: '#FF9800', '&:hover': { borderColor: '#F57C00', bgcolor: '#FFF3E0' } }}
                >
                  {isBackupLoading ? 'Restoring...' : 'Import Backup'}
                </Button>
                <input
                  type="file"
                  accept=".zip"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleImportBackup}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Developer Tools */}
      {showDevTools && (
        <Accordion sx={{ mt: 3, borderRadius: 3 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="dev-tools-content"
            id="dev-tools-header"
            sx={{ bgcolor: '#FFF3E0', borderRadius: '12px 12px 0 0' }}
          >
            <Typography variant="h6" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
              🛠️ Developer Tools
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ bgcolor: '#FAFAFA' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Testing utilities and debugging tools for developers (only visible in development mode)
                </Typography>
              </Grid>
              
              {/* Data Management */}
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#2196F3' }}>
                    📊 Data Management
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={devTools.populateTestData}
                      sx={{ borderRadius: 2 }}
                    >
                      Populate 30 Days Data
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={devTools.testAllMoods}
                      sx={{ borderRadius: 2 }}
                    >
                      Test All Moods
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={devTools.clearAllData}
                      sx={{ borderRadius: 2 }}
                    >
                      Clear All Data
                    </Button>
                  </Box>
                </Card>
              </Grid>
              
              {/* Streak & Rewards */}
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#4CAF50' }}>
                    🏆 Streak & Rewards
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={devTools.simulateLongStreak}
                      sx={{ borderRadius: 2 }}
                    >
                      Simulate 15-Day Streak
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => devTools.addMedals(5)}
                      sx={{ borderRadius: 2 }}
                    >
                      Add 5 Medals
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={devTools.showMedalDialog}
                      sx={{ borderRadius: 2 }}
                    >
                      Show Medal Dialog
                    </Button>
                  </Box>
                </Card>
              </Grid>
              
              {/* Notifications */}
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#FF5722' }}>
                    🔔 Notifications
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={devTools.testNotification}
                      sx={{ borderRadius: 2 }}
                    >
                      Test Notification
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={devTools.resetDailyReminder}
                      sx={{ borderRadius: 2 }}
                    >
                      Reset Daily Reminder
                    </Button>
                  </Box>
                </Card>
              </Grid>
              
              {/* UI Testing */}
              <Grid item xs={12} sm={6}>
                <Card sx={{ p: 2, height: '100%' }}>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#9C27B0' }}>
                    🎨 UI Testing
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={devTools.showMonthlyViewDialog}
                      sx={{ borderRadius: 2 }}
                    >
                      Show Monthly View
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSnackbarMessage('Test notification message!');
                        setSnackbarSeverity('warning');
                        setOpenSnackbar(true);
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      Test Snackbar
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setShowDevTools(false)}
                      color="error"
                      sx={{ borderRadius: 2 }}
                    >
                      Hide Dev Tools
                    </Button>
                  </Box>
                </Card>
              </Grid>
              
              {/* Current State Info */}
              <Grid item xs={12}>
                <Card sx={{ p: 2, bgcolor: '#F5F5F5' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    📋 Current State
                  </Typography>
                  <Typography variant="caption" component="div">
                    Check-ins: {checkInHistory.length} | Streak: {streak} | Medals: {medals}
                  </Typography>
                  <Typography variant="caption" component="div">
                    Moods recorded: {Object.keys(moodHistory).length}
                  </Typography>
                  <Typography variant="caption" component="div">
                    Today checked in: {hasCheckedInToday ? 'Yes' : 'No'}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <Alert 
          severity={snackbarSeverity} 
          onClose={() => setOpenSnackbar(false)}
          sx={{ borderRadius: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DailyCheckInNew;